---
title: Inbox and triage
summary: "The Inbox holds every conversation and email the business receives. On Triage, the AI sorts each new message and a person approves, changes or dismisses what it proposed."
visibility: authenticated
feature: inbox
audience: end-user
section: Pages
order: 30
last_verified: 2026-10-21
owner: docs
routes: ['/inbox', '/inbox/:conversationId']
aliases: [email, emails, mail, mailbox, messages, threads, conversations, triage, WhatsApp group]
tour:
  - anchor: inbox.toolbar.tabs
    text: "Open lists live threads, Triage lists messages waiting for a decision, oldest first. Resolved and Archived hold the rest."
  - anchor: inbox.toolbar.channels
    text: "The channel chips narrow the list to email, WhatsApp groups or chat."
  - anchor: inbox.list.rows
    text: "Each row is a thread. Press one to read it."
  - anchor: inbox.triage.panel
    text: "The Triage panel says what the AI made of each message and what approving would do."
    when_missing: "The open thread was not triaged."
  - anchor: inbox.triage.approve
    text: "Approve accepts the proposal and runs it, for example creating the quote or importing the schedule."
    when_missing: "Nothing on this thread waits for a decision, or you may not decide."
  - anchor: inbox.triage.change-kind
    text: "Change kind picks another kind when the AI sorted the message wrongly."
    when_missing: "Nothing on this thread waits for a decision."
  - anchor: inbox.record.sidebar
    text: "The record a thread belongs to sits beside it, with its step and who it is waiting on."
    when_missing: "This thread has no record yet."
---

# Inbox and triage

The Inbox is where every incoming message lands: emails from connected mailboxes,
WhatsApp group messages, and chats. Each conversation is a thread. Messages from a
mailbox set up for triage are sorted by the AI as they arrive, and a person decides
what happens next.

## Finding a thread

- The tabs: **Open** (live threads), **Triage** (messages waiting for a decision,
  oldest first), **Resolved** and **Archived**.
- The channel chips narrow the list: **Email**, **WhatsApp group** and the chat channels.
- **Filters** narrows Triage by kind, by what a message waits for and by mailbox.
- Search finds a thread by its sender, subject or words.

## What triage proposes

Each triaged message is one block in the thread's **Triage** panel. The block says who
sent the message and when, with the first two lines of it, then the kind the AI
proposed (for example a quote request, a schedule, a supplier invoice) as a chip
marked with how it decided ("✦ 92%, model", or the rule that decided it). Under the
chip, "Approving would …" says what approving does, and "Why:" gives the AI's reason.
Last come the actions. A message the AI could not place waits as unknown, with the
closest kind it found, for a person to sort.

A message that has been decided shows its receipt: "approved by", or "changed by", the
person's name and when. On a thread of several messages a decided block folds to that one
line, and pressing the line opens the block again; a thread of one message keeps its
block open under the receipt. A block that made or is about a record carries a record band, which stays visible on the receipt without opening the block: "Made" or "On"
the record's number and its state, the link to open it, **Not an enquiry** and
**Reinstate**.

## Approve, change or dismiss

- **Approve** accepts the proposed kind and runs what it does, with you as the person
  who did it. It can take up to a minute.
- **Change kind…** opens the **Kind** list. Pick the right kind and press **Apply**: it
  runs that kind instead. When the kind updates or closes a record and the thread has
  more than one, you also choose which record.
- **Dismiss** marks the message as nothing to act on.
- **Re-run** runs a kind again when it failed or made nothing.

Deciding needs the triage permission, and some kinds need their own (importing a
schedule needs the schedule import permission). When you lack one, the panel says
which and leaves the button off.

## Decide several at once

Tick the checkbox on each waiting or failed message you want to decide together. A bar
appears with the count, for
example "2 of 3 selected", and a button for each decision: Approve N, Change kind for
N…, Dismiss N, and Re-run N when every selected message failed. **Clear** drops the
selection. The box at the start of the bar ticks every message waiting.

- Approve N works only when every selected message can be approved as it stands. If
  one needs a record picked, an outcome or a kind, the bar says so and leaves Approve
  off. Approve N first asks you to confirm, listing what each kind would do; **Keep
  selecting** goes back.
- Dismiss N dismisses each message the way Dismiss does for one.
- Change kind for N… applies one kind to all of them; kinds that need a record picked
  or an outcome are decided one message at a time.
- A failed message is re-run, not approved.

The same bar is on the **Triage** tab: tick the threads waiting for a decision and
decide them from the list. Each thread is read again when you press the button
("Checking 3…"), and one that changed meanwhile or can't be decided that way is
skipped, with the reason beside the count.

Only one bulk decision runs at a time. When it ends, a message tells you how many
were decided and how many were skipped or refused.

## Create a quote from an email

A customer's request for a quote is proposed as a quote request. Press **Approve** on
its row: a quote record is created from the email, its fields extracted from the
message and its attachments, and the row then links to the new record ("Open quote …").
Open it to see the record, its **Original enquiry** and the quote process, which you
start from the record's process band. To create a quote by hand instead, use
**New quote record** on the Quotes page.

If a quote was created from a message that is not a real enquiry, press
**Not an enquiry** on the row: the record is closed and the thread resolved.
**Reinstate** undoes it.

## Approve a schedule from the Inbox

A schedule email (a subject such as `SCHEDULE W40(3)` with the schedule PDF attached)
is proposed as a schedule. Press **Approve** on it: the PDF is imported as a pending
revision and the thread says so, with a link to the pending import. Open it (or
**Fleet → Schedule**) to read the revision's preview and accept it there. Once the
revision is accepted, the thread is resolved on its own.

## The record beside a thread

A thread that belongs to a record (a quote) shows that record beside the messages:
its number, current step and who it is waiting on. On an email thread with several
records, choose which one to show. When a quote is prepared, its offer email waits on
the thread as a draft ("Offer email — review before sending"); review it and press
**Send**. Nothing is ever sent on its own.
